import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_URL } from "../../constants/urls";

interface Update {
  id: number;
  title: string;
  description: string;
  date: string;
  link: string;
}
// Announcements preserved from the deployed homepage, including its fallback feed.
const announcements: Update[] = [
  [
    16,
    "Merge two videos into one",
    "Choose two short clips, set their order, and download one MP4 with audio preserved.",
    "2026-10-05T12:00:00-04:00",
    "video-merger",
  ],
  [
    15,
    "Meet Nuhman Techies",
    "Ask technical questions, share code, and learn together. Create a developer profile and join the conversation.",
    "2026-09-26T12:00:00-04:00",
    "techies",
  ],
  [
    14,
    "JSON to TypeScript is here",
    "Generate typed interfaces from JSON, including nested objects, mixed arrays, and optional fields. Copy or download without uploading your data.",
    "2026-09-20T12:00:00-04:00",
    "json-to-typescript",
  ],
  [
    1,
    "New tool for Number Plate Blurring released",
    "Upload a video and we’ll automatically detect and blur license plates throughout the footage. Processing runs in the background and you can download the blurred video when it’s ready.",
    "2025-10-12",
    "plate-blur",
  ],
  [
    2,
    "New Keyword Processing tool added",
    "Extract, clean, and categorize keywords for SEO and analysis.",
    "2025-08-12",
    "keyword-extraction",
  ],
  [
    3,
    "Introduced JSON Formatter and Validator",
    "Pretty-print, minify, and validate JSON with error highlighting.",
    "2025-07-05",
    "json-formatter",
  ],
  [
    4,
    "JWT Token Generator now available",
    "Generate and inspect JWTs quickly for development and debugging.",
    "2025-06-21",
    "jwt-token-generator",
  ],
  [
    5,
    "UUID Generator added",
    "Create v4 UUIDs instantly with copy-to-clipboard support.",
    "2025-06-02",
    "uuid-generator",
  ],
  [
    6,
    "Regex Generator helps craft patterns",
    "Build and test regular expressions with live match preview.",
    "2025-05-15",
    "regex-generator",
  ],
  [
    7,
    "Compare Text tool released",
    "Side-by-side diff for two text blocks to spot changes easily.",
    "2025-05-01",
    "compare-text",
  ],
  [
    8,
    "QR Code Generator launched",
    "Create QR codes from text/URLs with download support.",
    "2025-04-18",
    "qr-code-generator",
  ],
  [
    9,
    "Invoice Generator is live",
    "Design simple invoices in the browser and export to PDF.",
    "2025-04-02",
    "invoice-generator",
  ],
  [
    10,
    "Business Name Generator added",
    "Brainstorm brand names with AI-assisted suggestions.",
    "2025-03-22",
    "business-name-generator",
  ],
  [
    11,
    "Roman Numeral Converter released",
    "Convert integers to Roman numerals and back.",
    "2025-03-05",
    "integer-roman",
  ],
  [
    12,
    "Time Zone Converter goes live",
    "Translate times across time zones with ease.",
    "2025-02-17",
    "time-zone-converter",
  ],
  [
    13,
    "Survey Builder Tool introduced",
    "Compose surveys with customizable questions and preview.",
    "2025-02-01",
    "survey-builder-tool",
  ],
].map(([id, title, description, date, path]) => ({
  id: Number(id),
  title: String(title),
  description: String(description),
  date: String(date),
  link: `/#/${path}`,
}));

function destination(link: string) {
  if (link.startsWith("/#/")) return link.slice(2);
  if (link.startsWith("/") && !link.startsWith("//")) return link;
  return /^https?:\/\//i.test(link) ? link : "/smart-tools";
}
export default function Updates({ all = false }: { all?: boolean }) {
  const [updates, setUpdates] = useState(announcements);
  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${API_URL}apis/nuhman/updates?size=50`, {
        signal: controller.signal,
        timeout: 10000,
      })
      .then(({ data }) => {
        const list = Array.isArray(data)
          ? data
          : data?.updates || data?.content;
        if (Array.isArray(list) && list.length) {
          const valid = list.filter(
            (item: any) =>
              typeof item.title === "string" &&
              typeof item.link === "string" &&
              typeof item.date === "string" &&
              !isNaN(Date.parse(item.date)),
          );
          if (valid.length)
            setUpdates(
              [
                ...announcements
                  .slice(0, 2)
                  .filter(
                    (local) =>
                      !valid.some(
                        (item: Update) =>
                          destination(item.link) === destination(local.link),
                      ),
                  ),
                ...valid,
              ].sort(
                (a: Update, b: Update) =>
                  Date.parse(b.date) - Date.parse(a.date),
              ),
            );
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);
  return (
    <section className="ed-updates" aria-labelledby="updates-heading">
      <div className="ed-section-heading">
        <div>
          <p className="ed-eyebrow">What’s new</p>
          <h2 id="updates-heading">Updates</h2>
        </div>
        {!all && <Link to="/smart-tools">View all ↗</Link>}
      </div>
      <div className="ed-update-list">
        {(all ? updates : updates.slice(0, 4)).map((item) => {
          const href = destination(item.link);
          return (
            <article className="ed-update" key={item.id || item.title}>
              <time dateTime={item.date}>
                {new Date(item.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  timeZone: "America/Toronto",
                })}
              </time>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
              {href.startsWith("https:") || href.startsWith("http:") ? (
                <a href={href} aria-label={`Explore: ${item.title}`}>
                  Explore ↗
                </a>
              ) : (
                <Link to={href} aria-label={`Explore: ${item.title}`}>
                  Explore ↗
                </Link>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
